import React from 'react';
import { useTranslation } from 'react-i18next';
import GenericTablePage from '../components/GenericTablePage';
import { GridColDef } from '@mui/x-data-grid';
import { Chip } from '@mui/material';
import { formatDate } from '../lib/dateUtils';
import { thumbImgProps } from '../lib/imageUtils';
import { stripHtml } from '../lib/htmlUtils';

const NewsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar' : 'en';

  const columns: GridColDef[] = [
    {
      field: 'cover_image_url',
      headerName: t('news.cover'),
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.value ? <img alt="" {...thumbImgProps(params.value, 44)} /> : null,
    },
    { field: 'title', headerName: t('news.newsTitle'), flex: 1, minWidth: 300 },
    { field: 'body', headerName: t('news.newsBody'), flex: 2, minWidth: 200, valueFormatter: (value) => stripHtml(value) },
    {
      field: 'is_public',
      headerName: t('news.isPublic'),
      width: 110,
      renderCell: (params) => params.value == null ? null : (
        <Chip
          label={params.value ? t('common.yes') : t('common.no')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'date',
      headerName: t('news.date'),
      width: 150,
      valueFormatter: (value) => formatDate(value, locale, 'medium'),
    },
  ];

  const fields = [
    {
      name: 'cover_image_url',
      label: t('news.cover'),
      type: 'image' as const,
      bucket: 'gallery',
      width: 'full' as const,
    },
    {
      name: 'title',
      label: t('news.newsTitle'),
      type: 'text' as const,
      required: true
    },
    { 
      name: 'body', 
      label: t('news.newsBody'), 
      type: 'textarea' as const, 
      required: true 
    },
    {
      name: 'date',
      label: t('news.publicationDate'),
      type: 'date' as const,
      required: true
    },
    {
      name: 'album_id',
      label: t('news.album'),
      width: 'half' as const,
      foreignKey: { table: 'photo_albums', valueField: 'id', labelField: 'name' },
    },
    {
      name: 'is_public',
      label: t('news.isPublic'),
      type: 'boolean' as const,
      width: 'half' as const,
    },
    {
      // Target recipients by city and/or classification.
      // Stored in news.to_recipients (jsonb) as [{type:'city'|'classification', id}].
      name: 'to_recipients',
      label: t('news.recipients'),
      type: 'targets' as const,
      width: 'full' as const,
      targets: [
        { type: 'city', label: t('news.targetCities'), table: 'cities', valueField: 'id', labelField: 'translation,city' },
        { type: 'classification', label: t('news.targetClassifications'), table: 'classifications', valueField: 'id', labelField: 'category,field,specialization' },
      ],
    },
  ];

  return (
    <GenericTablePage 
      tableName="news"
      detailPath={(id) => `/news/${id}`}
      editPath={(id) => (id ? `/news/${id}/edit` : '/news/new')}
      title={t('news.title')}
      columns={columns}
      fields={fields}
    />
  );
};

export default NewsPage;
